import {
  IBaseFactMeasures,
  isBaseFactMeasures,
} from '@principle-theorem/reporting/interfaces';
import { getKeyValues } from '@principle-theorem/shared';
import { FactTableInterface } from './base-measures';
import { AccountCreditEventFactMeasures } from './facts/account-credit-event-fact';
import { AppointmentEventFactMeasures } from './facts/appointment-event-fact';
import { AppointmentServiceCodeFactMeasures } from './facts/appointment-service-code-fact';
import { AppointmentTreatmentFactMeasures } from './facts/appointment-treatment-fact';
import { GapEventFactMeasures } from './facts/gap-event-fact';
import { GapFilledFactMeasures } from './facts/gap-filled-fact';
import { InvoiceEventFactMeasures } from './facts/invoice-event-fact';
import { LabJobEventFactMeasures } from './facts/lab-job-event-fact';
import { LabJobReceivedFactMeasures } from './facts/lab-job-received-fact';
import { PatientEventFactMeasures } from './facts/patient-event-fact';
import { PatientInteractionEventFactMeasures } from './facts/patient-interaction-event-fact';
import { PaymentPlanEventFactMeasures } from './facts/payment-plan-event-fact';
import { ScheduleSummaryEventFactMeasures } from './facts/schedule-summary-event-fact';
import { SchedulingEventFactMeasures } from './facts/scheduling-event-fact';
import { TaskCompletedFactMeasures } from './facts/task-completed-fact';
import { TaskEventFactMeasures } from './facts/task-event-fact';
import { TransactionEventFactMeasures } from './facts/transaction-event-fact';
import { TreatmentPlanCompletedFactMeasures } from './facts/treatment-plan-completed-fact';
import { TreatmentPlanEventFactMeasures } from './facts/treatment-plan-event-fact';

const ROOT_FACT_TABLES = [
  new AppointmentEventFactMeasures(),
  new GapEventFactMeasures(),
  new GapFilledFactMeasures(),
  new InvoiceEventFactMeasures(),
  new LabJobEventFactMeasures(),
  new ScheduleSummaryEventFactMeasures(),
  new AccountCreditEventFactMeasures(),
  new LabJobReceivedFactMeasures(),
  new PatientEventFactMeasures(),
  new PatientInteractionEventFactMeasures(),
  new PaymentPlanEventFactMeasures(),
  new TaskCompletedFactMeasures(),
  new TaskEventFactMeasures(),
  new TreatmentPlanCompletedFactMeasures(),
  new TreatmentPlanEventFactMeasures(),
  new TransactionEventFactMeasures(),
  new AppointmentServiceCodeFactMeasures(),
  new AppointmentTreatmentFactMeasures(),
  new SchedulingEventFactMeasures(),
];

export const FACT_TABLES = ROOT_FACT_TABLES.reduce(
  (all: FactTableInterface<unknown>[], factTable) => [
    ...all,
    factTable,
    ...getNestedFactTables(factTable),
  ],
  []
);

export function resolveFactTable(
  id: string
): FactTableInterface<unknown> | undefined {
  return FACT_TABLES.find((factTable) => factTable.table === id);
}

function getNestedFactTables(
  factTable: FactTableInterface<unknown>,
  depthLimit = 1,
  depth = 0
): FactTableInterface<unknown>[] {
  return getKeyValues(factTable)
    .map(([_key, value]) => value)
    .filter((value): value is IBaseFactMeasures => isBaseFactMeasures(value))
    .reduce((all: FactTableInterface<unknown>[], nestedFactTable) => {
      const nestedFactTables =
        depth < depthLimit
          ? getNestedFactTables(nestedFactTable, depthLimit, depth + 1)
          : [];
      return [...all, nestedFactTable, ...nestedFactTables];
    }, []);
}

export class FactTables {
  static get appointmentEvent(): AppointmentEventFactMeasures {
    return new AppointmentEventFactMeasures();
  }

  static get appointmentServiceCodeEvent(): AppointmentServiceCodeFactMeasures {
    return new AppointmentServiceCodeFactMeasures();
  }

  static get appointmentTreatmentEvent(): AppointmentTreatmentFactMeasures {
    return new AppointmentTreatmentFactMeasures();
  }

  static get gapEvent(): GapEventFactMeasures {
    return new GapEventFactMeasures();
  }

  static get gapFilled(): GapFilledFactMeasures {
    return new GapFilledFactMeasures();
  }

  static get invoiceEvent(): InvoiceEventFactMeasures {
    return new InvoiceEventFactMeasures();
  }

  static get labJobEvent(): LabJobEventFactMeasures {
    return new LabJobEventFactMeasures();
  }

  static get scheduleSummaryEvent(): ScheduleSummaryEventFactMeasures {
    return new ScheduleSummaryEventFactMeasures();
  }

  static get accountCreditEvent(): AccountCreditEventFactMeasures {
    return new AccountCreditEventFactMeasures();
  }

  static get labJobReceived(): LabJobReceivedFactMeasures {
    return new LabJobReceivedFactMeasures();
  }

  static get patientEvent(): PatientEventFactMeasures {
    return new PatientEventFactMeasures();
  }

  static get patientInteractionEvent(): PatientInteractionEventFactMeasures {
    return new PatientInteractionEventFactMeasures();
  }

  static get paymentPlanEvent(): PaymentPlanEventFactMeasures {
    return new PaymentPlanEventFactMeasures();
  }

  static get taskCompleted(): TaskCompletedFactMeasures {
    return new TaskCompletedFactMeasures();
  }

  static get taskEvent(): TaskEventFactMeasures {
    return new TaskEventFactMeasures();
  }

  static get treatmentPlanCompleted(): TreatmentPlanCompletedFactMeasures {
    return new TreatmentPlanCompletedFactMeasures();
  }

  static get treatmentPlanEvent(): TreatmentPlanEventFactMeasures {
    return new TreatmentPlanEventFactMeasures();
  }

  static get transactionEvent(): TransactionEventFactMeasures {
    return new TransactionEventFactMeasures();
  }

  static get schedulingEvent(): SchedulingEventFactMeasures {
    return new SchedulingEventFactMeasures();
  }
}
